/**
 * The driver-approval preconditions, as one list.
 *
 * MIRROR of the backend's `src/services/driverApprovalService.ts` — the backend is the authority
 * and enforces this on every approval, including the ManagerApp's one-press enroll-and-approve.
 * This copy exists only because the two repos share no package; keep the keys, order and wording
 * identical, and the test beside this file pins them.
 *
 * The gate is what the DRIVER has done — documents uploaded, terms accepted, permissions granted.
 * It is NOT whether those documents are marked verified: approving IS the verification act, and
 * the backend writes rcStatus / dlStatus / isProfilePhotoVerified when it stamps the approval
 * (owner ruling 2026-08-16). Gating on them was circular.
 */

export const MANDATORY_PERMISSION_IDS = ['draw', 'autostart', 'notification', 'background_location'] as const;

export type ApprovalConditionKey =
    | 'rcAvailable'
    | 'dlAvailable'
    | 'photoAvailable'
    | 'termsAccepted'
    | 'permissionsGranted';

export interface ApprovalCondition {
    key: ApprovalConditionKey;
    label: string;
    met: boolean;
}

export interface ApprovalConditionInput {
    isRCAvailable?: boolean;
    isDLAvailable?: boolean;
    isProfilePhotoAvailable?: boolean;
    isTermsAndConditionAccepted?: boolean;
    mandatoryPermissions?: unknown;
}

export const arePermissionsGranted = (mandatoryPermissions: unknown): boolean => {
    if (!mandatoryPermissions || typeof mandatoryPermissions !== 'object') return false;
    const granted = mandatoryPermissions as Record<string, unknown>;
    return MANDATORY_PERMISSION_IDS.every((id) => granted[id] === true);
};

export const evaluateApprovalConditions = (d: ApprovalConditionInput): ApprovalCondition[] => [
    { key: 'rcAvailable', label: 'RC uploaded', met: d.isRCAvailable === true },
    { key: 'dlAvailable', label: 'DL uploaded', met: d.isDLAvailable === true },
    { key: 'photoAvailable', label: 'Profile photo uploaded', met: d.isProfilePhotoAvailable === true },
    { key: 'termsAccepted', label: 'Terms & conditions accepted', met: d.isTermsAndConditionAccepted === true },
    { key: 'permissionsGranted', label: 'App permissions granted', met: arePermissionsGranted(d.mandatoryPermissions) },
];

export const unmetApprovalConditions = (d: ApprovalConditionInput): ApprovalCondition[] =>
    evaluateApprovalConditions(d).filter((c) => !c.met);

export const isApprovable = (d: ApprovalConditionInput): boolean =>
    evaluateApprovalConditions(d).every((c) => c.met);
